import { useEffect, useRef } from "react"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { routes, type ApiError, type StoreCode } from "@nova-ai/shared"
import {
  galleryStudioBootstrapInputsSchema,
  galleryDraftJobSchema,
  galleryDraftPreviewSchema,
  galleryStudioCandidateHistoryPageSchema,
  galleryStudioPlanInputsSchema,
  galleryStudioSlotPlanResultSchema,
  type GalleryDraftIdentity,
  type GalleryDraftJob,
  type GalleryDraftPreview,
  type GalleryStudioFormat,
  type GalleryStudioBootstrapInputs,
  type GalleryStudioCandidate,
  type GalleryStudioPlanInputs,
  type GalleryStudioPlanSlot,
} from "@nova-ai/shared/gallery-studio"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

export type {
  GalleryDraftIdentity,
  GalleryDraftJob,
  GalleryDraftPreview,
  GalleryStudioFormat,
  GalleryStudioBootstrapInputs,
  GalleryStudioPlanInputs,
  GalleryStudioPlanSlot,
}

export class GalleryStudioApiError extends Error {
  readonly kind: ApiError["kind"]
  readonly details: unknown

  constructor(error: ApiError) {
    super(error.message)
    this.name = "GalleryStudioApiError"
    this.kind = error.kind
    this.details = error.details
  }
}

const RETRYABLE_GALLERY_STUDIO_ERROR_KINDS = new Set<ApiError["kind"]>([
  "network",
  "unavailable",
  "upstream",
  "unknown",
])

function resultValue<Value>(result: Awaited<ReturnType<typeof apiFetch<Value>>>): Value {
  if (!result.ok) {
    throw new GalleryStudioApiError(result.error)
  }
  return result.value
}

export function useGalleryStudioBootstrapInputs(
  configSku: string,
  store: StoreCode,
  enabled: boolean
) {
  return useQuery({
    queryKey: queryKeys.galleryStudioBootstrapInputs(configSku, store),
    enabled: enabled && configSku.length > 0,
    queryFn: async () =>
      galleryStudioBootstrapInputsSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.bootstrapInputs({ configSku }), { query: { store } })
        )
      ),
  })
}

export function useCreateGalleryStudioIntent(configSku: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (targetSimpleSkus: string[]) =>
      resultValue(
        await apiFetch(routes.galleryStudio.intents(), {
          method: "POST",
          body: { configSku, targetSimpleSkus },
        })
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
      }),
  })
}

export function shouldRetryGalleryStudioPlanInputs(failureCount: number, error: Error): boolean {
  return (
    error instanceof GalleryStudioApiError &&
    RETRYABLE_GALLERY_STUDIO_ERROR_KINDS.has(error.kind) &&
    failureCount < 3
  )
}

export function useGalleryStudioPlanInputs(configSku: string, store: StoreCode) {
  return useInfiniteQuery({
    queryKey: queryKeys.galleryStudioPlanInputs(configSku, store),
    enabled: configSku.length > 0,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      galleryStudioPlanInputsSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.planInputs({ configSku }), {
            query: {
              store,
              documentLimit: 120,
              ...(pageParam ? { afterDocumentId: pageParam } : {}),
            },
          })
        )
      ),
    retry: shouldRetryGalleryStudioPlanInputs,
    getNextPageParam: (lastPage) => lastPage.existingDraftsPage.nextCursor,
    select: (data) => {
      const firstPage = data.pages[0]
      const lastPage = data.pages.at(-1)
      if (!firstPage || !lastPage) {
        throw new Error("Gallery Studio plan inputs returned no pages")
      }
      return {
        ...firstPage,
        existingDrafts: data.pages.flatMap((page) => page.existingDrafts),
        existingDraftsPage: lastPage.existingDraftsPage,
      }
    },
  })
}

export function useGalleryStudioDocumentCandidates(
  configSku: string,
  documentId: string,
  initialCandidates: GalleryStudioCandidate[],
  initialNextCursor: string | null
) {
  return useInfiniteQuery({
    queryKey: queryKeys.galleryStudioDocumentCandidates(configSku, documentId),
    enabled: configSku.length > 0 && documentId.length > 0,
    initialPageParam: null as string | null,
    initialData: {
      pages: [{ candidates: initialCandidates, nextCursor: initialNextCursor }],
      pageParams: [null],
    },
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async ({ pageParam }) =>
      galleryStudioCandidateHistoryPageSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.documentCandidates({ configSku, documentId }), {
            query: {
              limit: 12,
              ...(pageParam ? { beforeId: pageParam } : {}),
            },
          })
        )
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    select: (data) => data.pages.flatMap((page) => page.candidates),
  })
}

export function useGalleryStudioActions(configSku: string) {
  const queryClient = useQueryClient()
  const rereadSlotPlan = useMutation({
    mutationFn: async () => {
      await queryClient.refetchQueries(
        {
          queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
          type: "active",
        },
        { throwOnError: true }
      )
    },
  })
  const updateSlots = useMutation({
    mutationFn: async (input: {
      expectedVersion: number
      secondarySlots: { id: string; name: string }[]
    }) =>
      galleryStudioSlotPlanResultSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.slots({ configSku }), {
            method: "PATCH",
            body: input,
          })
        )
      ),
  })
  const savePlan = useMutation({
    mutationFn: async (input: {
      expectedVersion: number
      secondarySlots: GalleryStudioPlanSlot[]
    }) =>
      resultValue<{ version: number }>(
        await apiFetch(routes.galleryStudio.plans({ configSku }), { method: "POST", body: input })
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
      }),
  })
  const preview = useMutation({
    mutationFn: async (input: {
      expectedPlanVersion: number
      regenerateItems: GalleryDraftIdentity[]
    }) =>
      galleryDraftPreviewSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.launchPreview({ configSku }), {
            method: "POST",
            body: input,
          })
        )
      ),
  })
  const launch = useMutation({
    mutationFn: async (input: { previewId: string; previewHash: string }) =>
      galleryDraftJobSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.draftJobs({ configSku }), {
            method: "POST",
            body: { ...input, confirmation: "generate-unselected-gallery-drafts" },
          })
        )
      ),
  })
  const cancel = useMutation({
    mutationFn: async (jobId: string) =>
      resultValue(
        await apiFetch(routes.galleryStudio.cancelDraftJob({ configSku, jobId }), {
          method: "POST",
          body: { confirmation: "stop-before-next-paid-call" },
        })
      ),
  })
  const generateAnchor = useMutation({
    mutationFn: async (contractId: string) =>
      resultValue(
        await apiFetch(routes.galleryStudio.identityAnchorAttempts({ configSku, contractId }), {
          method: "POST",
          body: { confirmation: "generate-paid-identity-anchor" },
        })
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
      }),
  })
  const approveAnchor = useMutation({
    mutationFn: async (input: { contractId: string; attemptId: string }) =>
      resultValue(
        await apiFetch(
          routes.galleryStudio.approveIdentityAnchor({
            configSku,
            contractId: input.contractId,
            attemptId: input.attemptId,
          }),
          { method: "POST", body: { confirmation: "approve-identity-anchor" } }
        )
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
      }),
  })
  const selectFamilyMaster = useMutation({
    mutationFn: async (input: {
      slotId: string
      marketplace: string
      format: GalleryStudioFormat
      simpleSku: string
      identityContractId: string
      anchorAttemptId: string
      sceneRequestId: string
      plateId: string
      candidateId: string
    }) =>
      resultValue(
        await apiFetch(routes.galleryStudio.familyMasters({ configSku }), {
          method: "POST",
          body: { ...input, confirmation: "approve-family-master" },
        })
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
      }),
  })
  const rejectScene = useMutation({
    mutationFn: async (input: {
      plateId: string
      failureClass:
        | "required_text"
        | "wrong_identity"
        | "invented_content"
        | "master_leakage"
        | "scene_branding"
        | "composition"
        | "other"
      reason: string
    }) =>
      resultValue(
        await apiFetch(routes.galleryStudio.rejectScene({ configSku, plateId: input.plateId }), {
          method: "POST",
          body: { failureClass: input.failureClass, reason: input.reason },
        })
      ),
  })
  return {
    updateSlots,
    rereadSlotPlan,
    savePlan,
    preview,
    launch,
    cancel,
    generateAnchor,
    approveAnchor,
    selectFamilyMaster,
    rejectScene,
  }
}

export function useGalleryDraftJob(configSku: string, jobId: string) {
  const queryClient = useQueryClient()
  const refreshedTerminalJob = useRef("")
  const query = useInfiniteQuery({
    queryKey: queryKeys.galleryDraftJob(configSku, jobId),
    enabled: configSku.length > 0 && jobId.length > 0,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      galleryDraftJobSchema.parse(
        resultValue(
          await apiFetch(routes.galleryStudio.draftJob({ configSku, jobId }), {
            query: {
              analysisAttemptLimit: 50,
              ...(pageParam ? { beforeAnalysisAttemptId: pageParam } : {}),
            },
          })
        )
      ),
    getNextPageParam: (lastPage) => lastPage.analysisEvidencePage.nextCursor,
    select: (data) => mergeGalleryDraftJobPages(data.pages),
    refetchInterval: (query) => {
      const status = query.state.data?.pages[0]?.status
      return status === "queued" || status === "running" || status === "cancelling" ? 1_500 : false
    },
  })
  useEffect(() => {
    const status = query.data?.status
    if (
      jobId &&
      status &&
      !["queued", "running", "cancelling"].includes(status) &&
      refreshedTerminalJob.current !== jobId
    ) {
      refreshedTerminalJob.current = jobId
      void refreshGalleryStudioTerminalQueries(queryClient, configSku)
    }
  }, [configSku, jobId, query.data?.status, queryClient])
  return query
}

export function mergeGalleryDraftJobPages(pages: GalleryDraftJob[]): GalleryDraftJob {
  const firstPage = pages[0]
  const lastPage = pages.at(-1)
  if (!firstPage || !lastPage) {
    throw new Error("Gallery draft job returned no evidence pages")
  }
  return {
    ...firstPage,
    analysisEvidencePage: lastPage.analysisEvidencePage,
    items: firstPage.items.map((item) => {
      const attempts = pages.flatMap(
        (page) =>
          page.items.find((candidate) => candidate.id === item.id)?.analysisProviderEvidence ?? []
      )
      return {
        ...item,
        analysisProviderEvidence: [
          ...new Map(attempts.map((attempt) => [attempt.id, attempt])).values(),
        ],
      }
    }),
  }
}

export async function refreshGalleryStudioTerminalQueries(
  queryClient: QueryClient,
  configSku: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.galleryStudioPlanInputsPrefix(configSku),
      refetchType: "all",
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.galleryStudioDocumentCandidatesPrefix(configSku),
      refetchType: "all",
    }),
  ])
}
